interface AudioState {
  currentUrl: string | null;
  isPlaying: boolean;
}

type Listener = (state: AudioState) => void;

class AudioManager {
  private audio: HTMLAudioElement;
  private state: AudioState = { currentUrl: null, isPlaying: false };
  private listeners = new Set<Listener>();

  constructor() {
    this.audio = new Audio();
    this.audio.onplay = () => this.update({ isPlaying: true });
    this.audio.onpause = () => this.update({ isPlaying: false });
    this.audio.onended = () => this.update({ isPlaying: false });
  }

  private notify() {
    this.listeners.forEach((l) => l(this.state));
  }

  private update(patch: Partial<AudioState>) {
    this.state = { ...this.state, ...patch };
    this.notify();
  }

  getState(): AudioState {
    return this.state;
  }

  subscribe(l: Listener) {
    this.listeners.add(l);
    l(this.state);
    return () => {
      this.listeners.delete(l);
    };
  }

  async playUrl(url: string): Promise<void> {
    if (this.audio.src !== url) {
      this.audio.pause();
      this.audio.src = url;
    }
    // Ensure subscribers know which URL is considered current even if src didn't change
    this.update({ currentUrl: url });
    await this.audio.play();
  }

  pause(): void {
    this.audio.pause();
    // Ensure immediate UI reflection even if onpause event is delayed
    this.update({ isPlaying: false });
  }
}

export const audioManager = new AudioManager();
